import TransactionStatus from "../../enums/TransactionStatus";
import DisputeStatus from "../../enums/DisputeStatus";
import WorkflowRequestStatus from "enums/WorkflowRequestStatus";
import { Chip, StatusGlyph } from "@outpave/ds";
import type { ChipTone } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import type TransactionsType from "enums/TransactionsType";
import { REIMBURSEMENT_TYPES } from "utils/transactions/transactionGroups";

interface RenderTransactionStatusProps {
  transactionStatus: TransactionStatus | string;
  disputeStatus?: string;
  workflowStatus?: WorkflowRequestStatus | string;
  /** Lets a paid reimbursement read "Paid" rather than the card ledger's "Posted". */
  transactionType?: string;
  smallPill?: boolean;
}

type StatusEntry = {
  /** Canonical English label. Also the `StatusGlyph` lookup key. */
  name: string;
  tone: ChipTone;
};

type StatusMapping = {
  [key in
    | TransactionStatus
    | DisputeStatus
    | "WORKFLOW_PENDING"
    | "WORKFLOW_REJECTED"
    | "REIMBURSEMENT_PAID"]: StatusEntry;
} & { default: StatusEntry };

/**
 * Tone stands for the state's meaning, not its mood: `positive` for a settled
 * end state, `negative` for a failure, `warning` for something waiting on a
 * person, `info` for work the system is doing on its own, and `neutral` for a
 * terminal non-event that is nobody's problem.
 */
const statusMapping: StatusMapping = {
  [TransactionStatus.PENDING]: { name: "Pending", tone: "warning" },
  [TransactionStatus.REVERSED]: { name: "Reversed", tone: "neutral" },
  [TransactionStatus.RETURNED]: { name: "Returned", tone: "neutral" },
  [TransactionStatus.CLOSED]: { name: "Closed", tone: "neutral" },
  [TransactionStatus.FAILED]: { name: "Failed", tone: "negative" },
  [TransactionStatus.DECLINED]: { name: "Declined", tone: "negative" },
  [TransactionStatus.CANCELED]: { name: "Canceled", tone: "neutral" },
  [TransactionStatus.COMPLETE]: { name: "Posted", tone: "positive" },
  [TransactionStatus.REFUNDED]: { name: "Refunded", tone: "neutral" },
  [TransactionStatus.PENDING_PAYMENT]: { name: "Processing", tone: "info" },
  [TransactionStatus.PENDING_BANK_ACCOUNT]: {
    name: "Pending Bank",
    tone: "warning",
  },
  [DisputeStatus.CREATED]: { name: "Disputed", tone: "warning" },
  [DisputeStatus.SUBMITTED]: { name: "Disputed", tone: "warning" },
  [DisputeStatus.WON]: { name: "Disputed", tone: "neutral" },
  [DisputeStatus.LOST]: { name: "Disputed", tone: "negative" },
  WORKFLOW_PENDING: { name: "Approval", tone: "warning" },
  WORKFLOW_REJECTED: { name: "Rejected", tone: "negative" },
  REIMBURSEMENT_PAID: { name: "Paid", tone: "positive" },
  [TransactionStatus.PAYMENT_FAIL]: { name: "Payment Fail", tone: "negative" },
  [TransactionStatus.PROCESSING_PAYMENT]: {
    name: "Processing Payment",
    tone: "info",
  },
  [TransactionStatus.INSUFFICIENT_FUNDS]: {
    name: "Insufficient Funds",
    tone: "negative",
  },
  [TransactionStatus.PERSONAL_PENDING]: {
    name: "Personal Pending",
    tone: "warning",
  },
  [TransactionStatus.PERSONAL_PROCESSING]: {
    name: "Personal Processing",
    tone: "info",
  },
  [TransactionStatus.PERSONAL_COMPLETE]: {
    name: "Personal Complete",
    tone: "positive",
  },
  [TransactionStatus.PERSONAL_FAILED]: {
    name: "Personal Failed",
    tone: "negative",
  },
  [TransactionStatus.PERSONAL_REFUNDED]: {
    name: "Personal Refunded",
    tone: "neutral",
  },
  [TransactionStatus.REFUND_PENDING]: {
    name: "Refund Processing",
    tone: "info",
  },
  [TransactionStatus.REFUND_FAIL]: { name: "Refund Failed", tone: "negative" },
  [TransactionStatus.REFUND_CANCELED]: {
    name: "Refund Canceled",
    tone: "neutral",
  },
  [TransactionStatus.REFUND_SUCCESS]: { name: "Refunded", tone: "neutral" },
  [TransactionStatus.PARTIALLY_REFUNDED]: {
    name: "Partially Refunded",
    tone: "neutral",
  },
  default: { name: "Unknown Status", tone: "neutral" },
};

/**
 * The canonical English label for a transaction status, so a filter listing
 * statuses reads the same words as the pill rendering them. Callers translate.
 */
export const transactionStatusLabel = (status: TransactionStatus): string =>
  (statusMapping[status] ?? statusMapping.default).name;

// A reimbursement is only ever pending on approval and declined by an approver, and complete means paid out.
const REIMBURSEMENT_KEYS: Partial<Record<string, keyof StatusMapping>> = {
  [TransactionStatus.PENDING]: "WORKFLOW_PENDING",
  [TransactionStatus.DECLINED]: "WORKFLOW_REJECTED",
  [TransactionStatus.COMPLETE]: "REIMBURSEMENT_PAID",
};

const statusKey = (status: string, isReimbursement: boolean): string =>
  (isReimbursement && REIMBURSEMENT_KEYS[status]) || status;

/** The label a reimbursement's status reads under, the same word its pill shows. */
export const reimbursementStatusLabel = (status: TransactionStatus): string =>
  (
    statusMapping[statusKey(status, true) as keyof StatusMapping] ??
    statusMapping.default
  ).name;

/** The pill a transaction shows, so every list and drawer derives it the same way. */
export const transactionStatusEntry = (
  transactionStatus: TransactionStatus | string,
  disputeStatus?: string,
  workflowStatus?: WorkflowRequestStatus | string,
  transactionType?: string,
): StatusEntry & { key: string } => {
  let key =
    disputeStatus ||
    statusKey(
      transactionStatus,
      REIMBURSEMENT_TYPES.includes(transactionType as TransactionsType),
    );
  // Only override if workflowStatus is "PENDING" or "REJECTED"
  if (workflowStatus === "PENDING" || workflowStatus === "REJECTED") {
    key = `WORKFLOW_${workflowStatus}`;
  }
  const entry =
    statusMapping[key as keyof StatusMapping] || statusMapping.default;
  return { key, ...entry };
};

export const RenderTransactionStatus: React.FC<
  RenderTransactionStatusProps
> = ({
  transactionStatus,
  disputeStatus,
  workflowStatus,
  transactionType,
  smallPill = false,
}) => {
  const { t } = useTranslation();
  const { name, tone } = transactionStatusEntry(
    transactionStatus,
    disputeStatus,
    workflowStatus,
    transactionType,
  );

  return (
    <Chip
      size={smallPill ? "sm" : "md"}
      tone={tone}
      // Keyed on the canonical English name rather than the rendered label, so
      // the glyph survives translation; the tone is the fallback family.
      leading={
        <StatusGlyph status={name} tone={tone} size={smallPill ? 12 : 14} />
      }
    >
      {t(name)}
    </Chip>
  );
};
